import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { usePostAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import { OfficeComment } from '@/models/common/OfficeComment';
import { formatDateTimeLocale } from '@/helpers/dateHelper';
import deleteIcon from '@/assets/icons/delete.svg';
import styles from './OfficeCommentsDialog.module.css';

interface AppendChildOfficeCommentDtoOut {
  parentId: number;
  childId: number;
  comment: string;
  showInPersonalArea: boolean;
}

interface SendChildOfficeCommentDtoOut {
  parentId: number;
  childId: number;
  commentIndex: number;
  isVerified: boolean;
}

interface SendOfficeCommentDtoIn {
  requestorEmail: string;
  emailSubject: string;
  emailHtmlContent: string;
}

interface UpdateChildOfficeCommentVisibilityDtoOut {
  parentId: number;
  childId: number;
  commentIndex: number;
  showInPersonalArea: boolean;
}

interface DeleteChildOfficeCommentDtoOut {
  parentId: number;
  childId: number;
  commentIndex: number;
  // The server deletes only if this is still the text at commentIndex.
  expectedComment: string;
}

interface OfficeCommentsDialogProps {
  open: boolean;
  onClose: () => void;
  parentId: number;
  childId: number;
  officeComments: OfficeComment[];
  onSuccess: () => void;
}

const OfficeCommentsDialog = ({
  open,
  onClose,
  parentId,
  childId,
  officeComments,
  onSuccess,
}: OfficeCommentsDialogProps) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendingIndex, setSendingIndex] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<SendOfficeCommentDtoIn | null>(null);
  const [pendingSendIndex, setPendingSendIndex] = useState<number | null>(null);
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  // The comment as it was when the office clicked delete - the list may refetch underneath the confirmation.
  const [pendingDelete, setPendingDelete] = useState<{ index: number; comment: OfficeComment } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: appendComment, isPending: isAppending } = usePostAuthApi<void, AppendChildOfficeCommentDtoOut>(
    'management/children/office-comment'
  );

  const { mutate: sendComment, isPending: isSending } = usePutAuthApi<SendOfficeCommentDtoIn, object, SendChildOfficeCommentDtoOut>(
    'management/children/send-office-comment'
  );

  const { mutate: updateVisibility, isPending: isUpdatingVisibility } = usePutAuthApi<void, object, UpdateChildOfficeCommentVisibilityDtoOut>(
    'management/children/office-comment-visibility'
  );

  const { mutate: deleteComment, isPending: isDeleting } = usePostAuthApi<void, DeleteChildOfficeCommentDtoOut>(
    'management/children/office-comment/delete'
  );

  const isBusy = isAppending || isSending || isUpdatingVisibility || isDeleting;

  const refetchChildren = () =>
    queryClient.refetchQueries({
      predicate: (query) => query.queryKey[0] === queryKeys.children.management[0],
    });

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;

    setDeleteError(null);
    setSuccessMessage(null);

    const body: DeleteChildOfficeCommentDtoOut = {
      parentId,
      childId,
      commentIndex: pendingDelete.index,
      expectedComment: pendingDelete.comment.comment,
    };

    deleteComment(body, {
      onSuccess: () => {
        setPendingDelete(null);
        setSuccessMessage(t('officeComments.commentDeleted'));
        refetchChildren();
        onSuccess();
      },
      onError: (err: unknown) => {
        console.error('Error deleting office comment:', err);
        const axiosError = err as { response?: { status?: number } };
        // 409: the list changed since it was loaded, so the index no longer points at this comment.
        // Close the confirmation and reload the list - the office picks again from what is there now.
        if (axiosError.response?.status === 409) {
          setPendingDelete(null);
          setError(t('officeComments.commentsChanged'));
          refetchChildren();
          onSuccess();
        } else {
          setDeleteError(t('officeComments.errorDeletingComment'));
        }
      },
    });
  };

  const handleAddComment = (andSend: boolean) => {
    if (!newComment.trim()) {
      setError(t('officeComments.commentRequired'));
      return;
    }

    setError(null);
    setSuccessMessage(null);

    const body: AppendChildOfficeCommentDtoOut = {
      parentId,
      childId,
      comment: newComment.trim(),
      showInPersonalArea,
    };

    appendComment(
      body,
      {
        onSuccess: () => {
          setNewComment('');
          setShowInPersonalArea(true);
          setSuccessMessage(t('officeComments.commentAdded'));
          queryClient.refetchQueries({
            predicate: (query) => 
              query.queryKey[0] === queryKeys.children.management[0],
          });
          onSuccess();

          if (andSend) {
            // The new comment will be at the end of the list
            const newIndex = officeComments.length;
            handleSendComment(newIndex);
          }
        },
        onError: (err: unknown) => {
          console.error('Error adding office comment:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorAddingComment'));
        },
      }
    );
  };

  const handleSendComment = (index: number) => {
    setError(null);
    setSuccessMessage(null);
    setSendingIndex(index);

    const body: SendChildOfficeCommentDtoOut = {
      parentId,
      childId,
      commentIndex: index,
      isVerified: false, // First call is just for preview
    };

    sendComment(
      { body },
      {
        onSuccess: (data) => {
          setSendingIndex(null);
          setPreviewData(data);
          setPendingSendIndex(index);
        },
        onError: (err: unknown) => {
          console.error('Error getting email preview:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorSendingComment'));
          setSendingIndex(null);
        },
      }
    );
  };

  const handleConfirmSend = () => {
    if (pendingSendIndex === null) return;

    setError(null);
    setSendingIndex(pendingSendIndex);

    const body: SendChildOfficeCommentDtoOut = {
      parentId,
      childId,
      commentIndex: pendingSendIndex,
      isVerified: true, // Actual send
    };

    sendComment(
      { body },
      {
        onSuccess: () => {
          setSendingIndex(null);
          setPreviewData(null);
          setPendingSendIndex(null);
          setSuccessMessage(t('officeComments.commentSent'));
          queryClient.refetchQueries({
            predicate: (query) => 
              query.queryKey[0] === queryKeys.children.management[0],
          });
          onSuccess();
        },
        onError: (err: unknown) => {
          console.error('Error sending office comment:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorSendingComment'));
          setSendingIndex(null);
          setPreviewData(null);
          setPendingSendIndex(null);
        },
      }
    );
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    setPendingSendIndex(null);
  };

  const handleToggleVisibility = (index: number, currentValue: boolean) => {
    setError(null);
    setSuccessMessage(null);

    const body: UpdateChildOfficeCommentVisibilityDtoOut = {
      parentId,
      childId,
      commentIndex: index,
      showInPersonalArea: !currentValue,
    };

    updateVisibility(
      { body },
      {
        onSuccess: () => {
          setSuccessMessage(t('officeComments.visibilityUpdated'));
          queryClient.refetchQueries({
            predicate: (query) => 
              query.queryKey[0] === queryKeys.children.management[0],
          });
          onSuccess();
        },
        onError: (err: unknown) => {
          console.error('Error toggling visibility:', err);
          const axiosError = err as { response?: { data?: string } };
          setError(axiosError.response?.data || t('officeComments.errorTogglingVisibility'));
        },
      }
    );
  };

  const handleClose = () => {
    setNewComment('');
    setShowInPersonalArea(true);
    setError(null);
    setSuccessMessage(null);
    setPreviewData(null);
    setPendingSendIndex(null);
    setPendingDelete(null);
    setDeleteError(null);
    onClose();
  };

  // Preview dialog
  if (previewData) {
    return (
      <MainDialogLayout
        open={open}
        onOpenChange={(isOpen: boolean) => !isOpen && handleCancelPreview()}
        title={t('officeComments.confirmSendTitle')}
      >
        <div className={styles.previewContent}>
          <p className="body-md-regular text-(--font-body-1)">
            {previewData.requestorEmail ? (
              <Trans
                i18nKey="officeComments.confirmSendMessageTo"
                values={{ email: previewData.requestorEmail }}
                components={{ email: <bdi dir="ltr" className="body-md-semibold" /> }}
              />
            ) : (
              t('officeComments.confirmSendMessage')
            )}
          </p>

          <div className={styles.emailPreview}>
            <div className={styles.emailSubject}>
              <strong>{t('officeComments.subject')}:</strong> {previewData.emailSubject}
            </div>
            <div className={styles.emailBody} dangerouslySetInnerHTML={{ __html: previewData.emailHtmlContent }} />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button className={styles.secondaryButton} onClick={handleCancelPreview} disabled={isSending}>
              {t('cancel')}
            </button>
            <button className={styles.primaryButton} onClick={handleConfirmSend} disabled={isSending}>
              {isSending ? t('loading') : t('officeComments.sendEmail')}
            </button>
          </div>
        </div>
      </MainDialogLayout>
    );
  }

  const renderCommentsTable = (comments: OfficeComment[]) => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>{t('officeComments.comment')}</th>
          <th>{t('officeComments.date')}</th>
          <th>{t('officeComments.createdBy')}</th>
          <th>{t('officeComments.visibility')}</th>
          <th>{t('officeComments.sendStatus')}</th>
          <th aria-label={t('officeComments.deleteComment')} />
        </tr>
      </thead>
      <tbody>
        {comments.map((comment, index) => (
          <tr key={index}>
            <td className={styles.noteCell} title={comment.comment}>
              {comment.comment}
            </td>
            <td className={styles.dateCell}>{formatDateTimeLocale(comment.date)}</td>
            <td>{comment.updatedBy || '-'}</td>
            <td className={styles.visibilityCell}>
              <button
                className={comment.showInPersonalArea ? styles.visibleButton : styles.hiddenButton}
                onClick={() => handleToggleVisibility(index, comment.showInPersonalArea)}
                disabled={isUpdatingVisibility}
                title={comment.showInPersonalArea ? t('officeComments.visibleInPersonalArea') : t('officeComments.showInPersonalArea')}
              >
                {comment.showInPersonalArea ? t('officeComments.hideFromUser') : t('officeComments.showToUser')}
              </button>
            </td>
            <td className={styles.statusCell}>
              {comment.wasSent ? (
                <div className={styles.sentInfo}>
                  <span>
                    {t('officeComments.sentOn')}: {comment.sendDate ? formatDateTimeLocale(comment.sendDate) : '-'}
                  </span>
                  <span>
                    {t('officeComments.sentBy')}: {comment.sentBy || '-'}
                  </span>
                </div>
              ) : (
                <button
                  className={styles.sendButton}
                  onClick={() => handleSendComment(index)}
                  disabled={sendingIndex === index || isAppending}
                >
                  {sendingIndex === index ? t('loading') : t('officeComments.sendToRequestor')}
                </button>
              )}
            </td>
            <td className={styles.deleteCell}>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => {
                  setDeleteError(null);
                  setPendingDelete({ index, comment });
                }}
                disabled={isBusy}
                title={t('officeComments.deleteComment')}
                aria-label={t('officeComments.deleteComment')}
              >
                <img src={deleteIcon} alt="" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Main dialog
  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && handleClose()} title={t('officeComments.title')}>
      <div className={styles.content}>
        {/* Comments Table */}
        <div className={styles.tableSection}>
          <h4 className="body-md-semibold text-(--font-heading)">{t('officeComments.existingComments')}</h4>
          {officeComments.length > 0 ? (
            renderCommentsTable(officeComments)
          ) : (
            <div className={styles.noComments}>{t('officeComments.noComments')}</div>
          )}
        </div>

        {/* Add new note section */}
        <div className={styles.addNoteSection}>
          <h4 className="body-md-semibold text-(--font-heading)">{t('officeComments.addComment')}</h4>
          <ToggleSwitch
            label={t('officeComments.showInPersonalArea')}
            checked={showInPersonalArea}
            onChange={setShowInPersonalArea}
          />
          <textarea
            className={styles.textarea}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('officeComments.commentPlaceholder')}
            rows={3}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

        <div className={styles.actions}>
          <button className={styles.secondaryButton} onClick={handleClose} disabled={isBusy}>
            {t('close')}
          </button>
          <button
            className={styles.tertiaryButton}
            onClick={() => handleAddComment(true)}
            disabled={isBusy || !newComment.trim()}
          >
            {isAppending ? t('loading') : t('officeComments.saveAndSend')}
          </button>
          <button
            className={styles.primaryButton}
            onClick={() => handleAddComment(false)}
            disabled={isBusy || !newComment.trim()}
          >
            {isAppending ? t('loading') : t('save')}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isDeleting) setPendingDelete(null);
        }}
        title={t('officeComments.confirmDeleteTitle')}
        message={pendingDelete?.comment.comment}
        confirmText="officeComments.deleteComment"
        cancelText="cancel"
        variant="danger"
        isLoading={isDeleting}
        error={deleteError}
        onConfirm={handleConfirmDelete}
      >
        {pendingDelete?.comment.wasSent && (
          <p className="body-sm-regular text-(--font-body-2)">{t('officeComments.deleteSentNote')}</p>
        )}
      </ConfirmationDialog>
    </MainDialogLayout>
  );
};

export default OfficeCommentsDialog;
